/**
 * Floating AI Button Component
 *
 * A floating action button that opens the AI chat in a popover.
 * Users who prefer minimal UI can use this instead of the sidebar.
 *
 * Thread state is managed by usePlasmaThread inside PlasmaAIChat.
 * This component only manages open/close state.
 */

import { cn } from "@plasma/utils/cn";
import { MessageSquare } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { logger, PortalEvents } from "~/lib/logger";

import { PlasmaAIChat } from "./plasma-ai-chat";

interface FloatingAIButtonProps {
  /** Optional class name for positioning overrides */
  className?: string;
}

export function FloatingAIButton({ className }: FloatingAIButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    const newState = !isOpen;
    if (newState) {
      logger.info(PortalEvents.AI_FLOATING_BUTTON_CLICKED);
      logger.info(PortalEvents.AI_CHAT_OPENED, { mode: "floating" });
    } else {
      logger.info(PortalEvents.AI_CHAT_CLOSED, { mode: "floating" });
    }
    setIsOpen(newState);
  };

  const handleClose = () => {
    logger.info(PortalEvents.AI_CHAT_CLOSED, { mode: "floating" });
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating button */}
      <Button
        aria-label="Open AI assistant"
        className={cn(
          "fixed right-6 bottom-6 h-14 w-14 rounded-full shadow-lg",
          "transition-all duration-200 hover:shadow-xl",
          "z-50",
          isOpen && "pointer-events-none scale-0 opacity-0",
          className
        )}
        onClick={handleToggle}
        size="icon"
        title="Open AI assistant"
        type="button"
      >
        <MessageSquare className="h-6 w-6" />
      </Button>

      {/* Chat panel */}
      {isOpen && (
        <div
          className={cn(
            "fixed right-3 bottom-6 left-3 z-50 h-[min(600px,80vh)] w-[calc(100%-1.5rem)] sm:right-6 sm:left-auto sm:w-[400px]",
            "bg-background rounded-xl border shadow-2xl",
            "flex flex-col overflow-hidden",
            "slide-in-from-bottom-4 fade-in animate-in duration-200"
          )}
        >
          {/* Chat component manages its own thread state and has close button in header */}
          <PlasmaAIChat onClose={handleClose} />
        </div>
      )}

      {/* Backdrop for click-outside-to-close */}
      {isOpen && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40"
          onClick={handleClose}
        />
      )}
    </>
  );
}
